import { zodResolver } from "@hookform/resolvers/zod";
import {
	Button,
	Group,
	NumberInput,
	Stack,
	Switch,
	TextInput,
} from "@mantine/core";
import { Controller, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";

import {
	type RewardDraft,
	rewardDraftSchema,
} from "@/features/referral/lib/referral-schemas";

export { emptyRewardDraft } from "@/features/referral/lib/referral-schemas";
export type { RewardDraft };

type RewardEditorFormProps = {
	draft: RewardDraft;
	saving: boolean;
	onCancel: () => void;
	onSave: (draft: RewardDraft) => void;
};

export function RewardEditorForm({
	draft,
	saving,
	onCancel,
	onSave,
}: RewardEditorFormProps) {
	const { t } = useTranslation();
	const { control, handleSubmit } = useForm<RewardDraft>({
		resolver: zodResolver(rewardDraftSchema),
		defaultValues: draft,
	});

	return (
		<form onSubmit={handleSubmit(onSave)}>
			<Stack>
				<Controller
					name="reward"
					control={control}
					render={({ field, fieldState }) => (
						<TextInput
							{...field}
							label={t("referral.item")}
							autoComplete="off"
							error={fieldState.error?.message}
						/>
					)}
				/>
				<Controller
					name="pointsCost"
					control={control}
					render={({ field, fieldState }) => (
						<NumberInput
							label={t("referral.pointsCost")}
							min={1}
							value={field.value}
							onChange={field.onChange}
							error={fieldState.error?.message}
						/>
					)}
				/>
				<Controller
					name="isActive"
					control={control}
					render={({ field }) => (
						<Switch
							label={t("common.active")}
							checked={field.value}
							onChange={(event) => field.onChange(event.currentTarget.checked)}
						/>
					)}
				/>
				<Group justify="flex-end">
					<Button variant="default" type="button" onClick={onCancel}>
						{t("common.cancel")}
					</Button>
					<Button loading={saving} type="submit">
						{t("common.save")}
					</Button>
				</Group>
			</Stack>
		</form>
	);
}
